import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { FilingStatus } from "./useProfile";

// =============================================================================
// Tax projection (computed) — wraps /api/tax/projection
// =============================================================================

/** A slice of bracket-based tax calculation surfaced by the engine. */
export interface BracketTaxResult {
  totalTax: number;
  slices: BracketSlice[];
}

export interface BracketSlice {
  rate: number;
  lowerBound: number;
  upperBound: number;
  amountInBracket: number;
  taxOnSlice: number;
}

export interface TaxProjectionMissingInput {
  fieldKey: string;
  label: string;
  why: string;
  isBlocking: boolean;
  routeHint: string | null;
}

export interface TaxProjection {
  taxYear: number;
  filingStatus: FilingStatus;
  stateCode: string;

  totalW2Wages: number;
  netScheduleC: number;
  otherIncome: number;
  totalIncome: number;

  halfSeTaxDeduction: number;
  seRetirementDeduction: number;
  seHealthInsuranceDeduction: number;
  hsaDeduction: number;
  iraDeduction: number;
  totalAdjustments: number;
  agi: number;

  standardOrItemizedDeduction: number;
  usedItemized: boolean;
  qbiDeduction: number;
  taxableIncome: number;

  federalIncomeTax: BracketTaxResult;
  seTaxTotal: number;
  seSocialSecurityPortion: number;
  seMedicarePortion: number;
  additionalMedicareTax: number;
  netInvestmentIncomeTax: number;
  totalFederalTax: number;

  wisconsinAgi: number;
  wisconsinStandardDeduction: number;
  wisconsinTaxableIncome: number;
  wisconsinIncomeTax: BracketTaxResult;
  totalStateTax: number;

  totalLiability: number;
  totalWithholdingYtd: number;
  totalEstimatedPaidYtd: number;
  remainingLiabilityToCover: number;

  effectiveTaxRateOnAgi: number;
  nextDollarFederalRate: number;
  nextDollarStateRate: number;
  nextDollarBlendedRate: number;

  missingInputs: TaxProjectionMissingInput[];
  warnings: string[];
  hasBlockingGaps: boolean;
}

export interface TaxProjectionResponse {
  projection: TaxProjection;
  marginalBusinessLiability: number;
}

export function useTaxProjection(year?: number) {
  return useQuery({
    queryKey: ["tax", "projection", year ?? "current"],
    queryFn: async (): Promise<TaxProjectionResponse> => {
      const r = await api().get<TaxProjectionResponse>("/api/tax/projection", {
        params: year ? { year } : undefined,
      });
      return r.data;
    },
    staleTime: 30_000,
  });
}

// =============================================================================
// Tax intake — per-section CRUD, all year-keyed
// =============================================================================

export type W2Person = "Owner" | "Spouse";

export interface PriorYearReturn {
  id: string;
  ownerUserId: string;
  taxYear: number;
  filingStatus: FilingStatus;
  line1aWages: number | null;
  line2bTaxableInterest: number | null;
  line3bOrdinaryDividends: number | null;
  line3aQualifiedDividends: number | null;
  line4bIraDistributions: number | null;
  line5bPensions: number | null;
  line7CapitalGain: number | null;
  line8OtherIncome: number | null;
  line10Adjustments: number | null;
  line11Agi: number | null;
  line12Deductions: number | null;
  line13QbiDeduction: number | null;
  line15TaxableIncome: number | null;
  line16Tax: number | null;
  line23OtherTaxes: number | null;
  line24TotalTax: number | null;
  line25Withholding: number | null;
  line26EstimatedPayments: number | null;
  scheduleCNetProfit: number | null;
  scheduleSeTax: number | null;
  scheduleSeDeductibleHalf: number | null;
  stateCode: string;
  stateTaxableIncome: number | null;
  stateTotalTax: number | null;
  stateWithholding: number | null;
  stateEstimatedPayments: number | null;
  notes: string;
}

export interface CurrentYearPersonal {
  id: string;
  ownerUserId: string;
  taxYear: number;
  ytdTaxableInterest: number | null;
  ytdOrdinaryDividends: number | null;
  ytdQualifiedDividends: number | null;
  ytdShortTermCapitalGain: number | null;
  ytdLongTermCapitalGain: number | null;
  ytdPassiveK1Income: number | null;
  ytdHsaContribution: number | null;
  ytdTraditionalIraContribution: number | null;
  projectedRemainingTaxableInterest: number | null;
  projectedRemainingOrdinaryDividends: number | null;
  projectedRemainingQualifiedDividends: number | null;
  projectedRemainingCapitalGain: number | null;
  ytdFederalEstimatedPaid: number | null;
  ytdStateEstimatedPaid: number | null;
  notes: string;
}

export interface W2Stub {
  id: string;
  ownerUserId: string;
  taxYear: number;
  personRole: W2Person;
  employerName: string;
  employerEin: string | null;
  asOfDate: string | null;
  ytdGrossWages: number | null;
  ytdFederalWithholding: number | null;
  ytdSocialSecurityWages: number | null;
  ytdSocialSecurityTaxWithheld: number | null;
  ytdMedicareWages: number | null;
  ytdMedicareTaxWithheld: number | null;
  ytdStateWithholding: number | null;
  projectedAnnualGrossWages: number | null;
  projectedAnnualFederalWithholding: number | null;
  projectedAnnualStateWithholding: number | null;
  ytdEmployee401kPretax: number | null;
  ytdSection125Pretax: number | null;
  notes: string;
}

export interface Carryforwards {
  id: string;
  ownerUserId: string;
  taxYear: number;
  federalNolCarryforward: number | null;
  longTermCapitalLossCarryforward: number | null;
  shortTermCapitalLossCarryforward: number | null;
  section179Disallowed: number | null;
  passiveActivityLossCarryforward: number | null;
  amtCreditCarryforward: number | null;
  stateNolCarryforward: number | null;
  stateCapitalLossCarryforward: number | null;
  notes: string;
}

export interface BusinessYtd {
  id: string;
  ownerUserId: string;
  taxYear: number;
  asOfDate: string | null;
  ytdGrossReceipts: number | null;
  ytdCostOfGoodsSold: number | null;
  ytdOrdinaryExpenses: number | null;
  ytdDepreciation: number | null;
  ytdHomeOfficeDeduction: number | null;
  projectedAnnualGrossReceipts: number | null;
  projectedAnnualOrdinaryExpenses: number | null;
  projectedAnnualDepreciation: number | null;
  projectedAnnualHomeOffice: number | null;
  ytdSCorpOwnerWages: number | null;
  projectedAnnualSCorpOwnerWages: number | null;
  ytdSolo401kEmployeeDeferral: number | null;
  ytdSolo401kEmployerProfitShare: number | null;
  ytdSepIraContribution: number | null;
  ytdBusinessHsaContribution: number | null;
  ytdSelfEmployedHealthInsurance: number | null;
  isSstb: boolean;
  notes: string;
}

const intakeKey = (section: string, year: number) => ["tax", "intake", section, year];

function invalidateProjection(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["tax", "projection"] });
  qc.invalidateQueries({ queryKey: ["strategy", "cash-allocation"] });
  qc.invalidateQueries({ queryKey: ["strategy", "entity-comparison"] });
  qc.invalidateQueries({ queryKey: ["advisor", "cards"] });
}

// ---------- Prior-year ---------------------------------------------------------

export function usePriorYearReturn(year: number) {
  return useQuery({
    queryKey: intakeKey("prior-year", year),
    queryFn: async (): Promise<PriorYearReturn> => {
      const r = await api().get<PriorYearReturn>(`/api/tax/intake/prior-year/${year}`);
      return r.data;
    },
  });
}

export function useUpdatePriorYearReturn(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PriorYearReturn) => {
      const r = await api().put<PriorYearReturn>(`/api/tax/intake/prior-year/${year}`, input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: intakeKey("prior-year", year) });
      invalidateProjection(qc);
    },
  });
}

// ---------- Current-year personal ---------------------------------------------

export function useCurrentYearPersonal(year: number) {
  return useQuery({
    queryKey: intakeKey("current-year-personal", year),
    queryFn: async (): Promise<CurrentYearPersonal> => {
      const r = await api().get<CurrentYearPersonal>(
        `/api/tax/intake/current-year-personal/${year}`,
      );
      return r.data;
    },
  });
}

export function useUpdateCurrentYearPersonal(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CurrentYearPersonal) => {
      const r = await api().put<CurrentYearPersonal>(
        `/api/tax/intake/current-year-personal/${year}`,
        input,
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: intakeKey("current-year-personal", year) });
      invalidateProjection(qc);
    },
  });
}

// ---------- W-2 stubs (list) --------------------------------------------------

export function useW2Stubs(year: number) {
  return useQuery({
    queryKey: intakeKey("w2", year),
    queryFn: async (): Promise<W2Stub[]> => {
      const r = await api().get<W2Stub[]>(`/api/tax/intake/w2/${year}`);
      return r.data;
    },
  });
}

export function useUpsertW2Stub(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: W2Stub) => {
      const r = await api().put<W2Stub>(`/api/tax/intake/w2/${year}`, input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: intakeKey("w2", year) });
      invalidateProjection(qc);
    },
  });
}

export function useDeleteW2Stub(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api().delete(`/api/tax/intake/w2/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: intakeKey("w2", year) });
      invalidateProjection(qc);
    },
  });
}

// ---------- Carryforwards -----------------------------------------------------

export function useCarryforwards(year: number) {
  return useQuery({
    queryKey: intakeKey("carryforwards", year),
    queryFn: async (): Promise<Carryforwards> => {
      const r = await api().get<Carryforwards>(`/api/tax/intake/carryforwards/${year}`);
      return r.data;
    },
  });
}

export function useUpdateCarryforwards(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Carryforwards) => {
      const r = await api().put<Carryforwards>(`/api/tax/intake/carryforwards/${year}`, input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: intakeKey("carryforwards", year) });
      invalidateProjection(qc);
    },
  });
}

// ---------- Business YTD ------------------------------------------------------

export function useBusinessYtd(year: number) {
  return useQuery({
    queryKey: intakeKey("business-ytd", year),
    queryFn: async (): Promise<BusinessYtd> => {
      const r = await api().get<BusinessYtd>(`/api/tax/intake/business-ytd/${year}`);
      return r.data;
    },
  });
}

export function useUpdateBusinessYtd(year: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BusinessYtd) => {
      const r = await api().put<BusinessYtd>(`/api/tax/intake/business-ytd/${year}`, input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: intakeKey("business-ytd", year) });
      invalidateProjection(qc);
    },
  });
}

// =============================================================================
// Phase C: Schedule A simulator + BLS CES benchmark overlay
// =============================================================================

export interface ScheduleASimulation {
  countedRows: number;
  saltPropertyTax: number;
  saltStateIncomeTax: number;
  saltSubTotalUncapped: number;
  saltAfterCap: number;
  saltCappedAway: number;
  saltCap: number;
  mortgageInterest: number;
  medicalRaw: number;
  medicalAgiFloor: number;
  medicalDeductible: number;
  charitableContributions: number;
  investmentInterest: number;
  otherPersonalExpenses: number;
  itemizedTotal: number;
  standardDeduction: number;
  itemizingBeatsStandard: boolean;
  itemizingAdvantage: number;
}

export interface ScheduleAResponse {
  simulation: ScheduleASimulation | null;
  blocked: boolean;
  reason?: string;
  routeHint?: string;
}

export function useScheduleASimulation(year?: number) {
  return useQuery({
    queryKey: ["tax", "schedule-a", year ?? "current"],
    queryFn: async (): Promise<ScheduleAResponse> => {
      const r = await api().get<ScheduleAResponse>("/api/tax/schedule-a", {
        params: year ? { year } : undefined,
      });
      return r.data;
    },
    staleTime: 30_000,
  });
}

export interface BlsCesProfile {
  incomeBracketLabel: string;
  averageAnnualExpenditures: number;
  foodAtHome: number;
  foodAwayFromHome: number;
  housingShelter: number;
  utilities: number;
  insurance: number;
  transportation: number;
  healthcareOutOfPocket: number;
  entertainment: number;
  apparel: number;
  propertyTaxes: number;
  mortgageInterest: number;
  cashContributions: number;
  incomeUsedForLookup: number;
}

export function useBlsBenchmark(preTaxIncome: number | null) {
  return useQuery({
    queryKey: ["tax", "bls-benchmark", preTaxIncome ?? 0],
    queryFn: async (): Promise<BlsCesProfile> => {
      const r = await api().get<BlsCesProfile>("/api/tax/bls-benchmark", {
        params: { preTaxIncome },
      });
      return r.data;
    },
    enabled: preTaxIncome != null && preTaxIncome > 0,
    staleTime: 5 * 60_000,
  });
}
